/**
 * Label printer formats (pure). The shop runs TWO label printers and both stay
 * (`docs/label-printer-formats-plan-v1.md` D1): a Rollo on 51 × 25 mm stock and a DYMO LabelWriter
 * 5XL on #30334 (2‑1/4″ × 1‑1/4″). Same layout, same type sizes, different paper — so the geometry
 * lives here as a handful of CSS custom properties and `label-sheet.tsx` keeps ONE stylesheet that
 * reads them through `var()`.
 *
 * Why custom properties rather than two stylesheets, or one stylesheet with the numbers interpolated:
 * every print rule in `LABEL_CSS` was measured on paper, and the comments above it record defects that
 * only appear on a printout (a filled bar coming off blank, gray dithering to a stipple, a two-class
 * selector stealing a page break). A second copy of that stylesheet would drift away from those notes;
 * interpolating forty values would make them unreadable. Six declarations swap, the rest is shared.
 *
 * ⚠️ The values are NOT a scale factor. Width and height differ by different ratios (+13% / +27%), and
 * type legibility does not scale linearly with either — `--sbody` actually goes DOWN on the larger
 * label (see its note below). Each value was measured, not derived.
 */

/** One printable label geometry. Every field is a CSS length, used verbatim inside `var()`. */
export interface LabelFormat {
  id: LabelFormatId;
  /** Operator-facing name, shown in the print toolbar. Names the PRINTER first — that is what the
   *  operator is choosing in Chrome's dialog, and the two must agree. */
  name: string;
  /** `@page { size: … }`. */
  page: string;
  cardW: string;
  cardH: string;
  padV: string;
  padH: string;
  qr: string;
  order: string;
  qrGap: string;
  sbody: string;
}

export type LabelFormatId = 'rollo51x25' | 'dymo30334';

export const LABEL_FORMATS: readonly LabelFormat[] = [
  {
    id: 'rollo51x25',
    name: 'Rollo — 51 × 25 mm',
    page: '51mm 25mm',
    cardW: '51mm',
    cardH: '25mm',
    padV: '1.3mm',
    padH: '1.6mm',
    // ⚠️ 17 mm holds a worst-case v4 payload at 0.415 mm/module = 3.3 dots on the Rollo's 203 dpi head.
    // That is FINER than the 0.486 mm/module the 2026-07-25 spike validated, because the spike's sample
    // was a v3 code and real payloads reach v4 (the store slug decides it: `teamobs` → v3,
    // `low-trendz` → v4). Unchanged here because that is what production prints today and D1 keeps the
    // Rollo as-is — but it is now measured rather than assumed, and raising it to 20 mm is an open
    // decision (plan § 9.2), costing ~3 mm of this format's 47.8 mm content width.
    qr: '17mm',
    order: '3.2mm',
    qrGap: '0.7mm',
    sbody: '6.4mm',
  },
  {
    id: 'dymo30334',
    name: 'DYMO 5XL — 30334 (2¼ × 1¼ in)',
    // ⚠️ EXACT INCHES, never the rounded `57mm 32mm`. 32 mm is 0.25 mm TALLER than the physical label,
    // and the point of an exact value is that Chrome hands the driver a size matching its named media
    // ("30334 Multipurpose, 2-1/4 x 1-1/4 in"). A near-miss lets Chrome pick the closest paper and
    // SCALE to fit — which still yields a plausible, scannable label at the wrong size, and is the only
    // failure mode here that is invisible on paper.
    page: '2.25in 1.25in',
    cardW: '2.25in',
    cardH: '1.25in',
    // 2 mm rather than the Rollo's 1.3: the DYMO has a larger unprintable leading edge. Content box
    // measures 53.6 × 27.8 mm.
    padV: '2mm',
    padH: '1.8mm',
    // ✅ LOCKED by the owner 2026-09-06 off the mockup's ladder. 20 mm holds a worst-case v4 payload at
    // 0.488 mm/module — i.e. it RESTORES the 0.486 the spike validated — and the 5XL's 300 dpi head
    // prints that at 5.8 dots/module instead of the 3.9 the spike measured on the Rollo.
    qr: '20mm',
    order: '3.6mm',
    qrGap: '0.8mm',
    // ⚠️ SMALLER than the Rollo's 6.4 mm, which is not a typo. This is the pile divider's blank-style
    // line, and enlarging it reduces character capacity FASTER than the extra width adds it. Measured
    // against the real stylesheet: at 6.4 mm neither `RICHARDSON 112` (54.0 mm) nor `COMFORT COLORS`
    // (59.0 mm) fits the 53.45 mm line; 5.8 mm fits the whole tail. It clears `COMFORT COLORS` by only
    // ~0.16 mm though, so this is the value to revisit first if a long blank name ever clips on paper —
    // 5.6 mm buys ~2 mm of slack for almost nothing.
    sbody: '5.8mm',
  },
] as const;

export const DEFAULT_LABEL_FORMAT_ID: LabelFormatId = 'rollo51x25';

export function labelFormatById(id: string | null | undefined): LabelFormat {
  return (
    LABEL_FORMATS.find((f) => f.id === id) ??
    // Non-null: DEFAULT_LABEL_FORMAT_ID is one of the ids above, asserted in label-format.test.ts.
    (LABEL_FORMATS.find((f) => f.id === DEFAULT_LABEL_FORMAT_ID) as LabelFormat)
  );
}

/**
 * The per-format custom properties, scoped to the print portal. Prepended to `LABEL_CSS`, which reads
 * every one through `var()` with no fallbacks — a missing property would silently collapse a length to
 * nothing, so `label-format.test.ts` asserts this block declares exactly the set the stylesheet uses.
 */
export function labelFormatVars(f: LabelFormat): string {
  return `
#label-print {
  --card-w: ${f.cardW}; --card-h: ${f.cardH};
  --pad-v: ${f.padV}; --pad-h: ${f.padH};
  --qr: ${f.qr}; --qr-gap: ${f.qrGap}; --order: ${f.order};
  --sbody: ${f.sbody};
}
`;
}

/**
 * The imperative `@page` rule, injected only between `beforeprint` and `afterprint`.
 *
 * ⚠️ The single most forgettable piece of this feature. Omit it and the label prints centred on Letter
 * with the global 12 mm margins (`globals.css`), which looks like a layout bug rather than a missing
 * rule. `margin: 0` is as load-bearing as `size`.
 */
export function labelPageRule(f: LabelFormat): string {
  return `@page { size: ${f.page}; margin: 0; }`;
}

const STORAGE_KEY = 'fsp.label.format.v1';

/**
 * The station's remembered format. `localStorage` is per-origin per-browser-profile, which is exactly
 * the granularity wanted: all three printers hang off one Windows bench (plan D2) as separate Chrome
 * profiles, so each profile remembers ITS printer's geometry with no per-station configuration.
 *
 * Reads and writes are wrapped because the accessor itself throws in a private window or with site data
 * blocked, and an unreadable preference must degrade to the default rather than break the print portal.
 */
export function loadLabelFormat(): LabelFormat {
  try {
    return labelFormatById(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return labelFormatById(DEFAULT_LABEL_FORMAT_ID);
  }
}

export function saveLabelFormatId(id: LabelFormatId): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // A station that cannot persist the choice still prints correctly for this session.
  }
}
